import React, { Suspense, useState } from "react";
import { formatPageSpan } from "./scopeDiscoveryApi";
import { formatExcelSourceRanges } from "./rfpUtils";
import { decisionItemReason, decisionItemTitle, decisionItems, decisionStatus, isOpenDecision, itemStillNeedsDecision } from "./productDecision";

const RfpProductDecisionDialog = React.lazy(() => import("./RfpProductDecisionDialog"));

function locationLabel(item) {
  if (item?.source_ranges?.length) return formatExcelSourceRanges(item.source_ranges);
  const pages = item?.pages || item?.primary_pages || [];
  return pages.length ? formatPageSpan(pages) : "—";
}

export default function RfpProductDecisionPanel({ jobId, scope, rows = null, onApplied, onShowInGrid }) {
  const items = decisionItems(scope);
  const [dialog, setDialog] = useState(null);
  if (!items.length) return null;
  const itemIsOpen = (item) => (Array.isArray(rows) ? itemStillNeedsDecision(item, rows) : isOpenDecision(item));
  const openItems = items.filter(itemIsOpen);

  return (
    <section className="rounded-xl border border-amber-300 bg-amber-50/70 p-4 dark:border-amber-800 dark:bg-amber-950/30" data-testid="rfp-product-decisions">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-950 dark:text-amber-100">
        Product decisions
        <span className="ml-2 font-normal text-amber-800/80 dark:text-amber-200/80">{openItems.length} open</span>
      </h3>
      <p className="mt-1 text-sm text-amber-950/80 dark:text-amber-100/80">
        These discoveries were not assigned to a product. Review the rows, then keep Gen Req or assign a product. You can include, skip, or change individual rows before confirming.
      </p>
      <div className="mt-3 space-y-3">
        {items.map((item) => {
          const open = itemIsOpen(item);
          const status = open
            ? decisionStatus(item)
            : item.product_decision?.choice
              ? decisionStatus(item)
              : "Rows already have a product.";
          return (
            <article key={item.item_id} className="rounded-lg border border-amber-200 bg-white px-3 py-2 dark:border-amber-900 dark:bg-slate-900" data-testid={`rfp-product-decision-${item.item_id}`}>
              <p className="text-xs uppercase tracking-wide text-slate-500">{item.sectionLabel}</p>
              <p className="font-medium text-slate-900 dark:text-slate-100">{decisionItemTitle(item)}</p>
              <p className="text-xs text-slate-500">{locationLabel(item)}</p>
              {decisionItemReason(item) ? <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">{decisionItemReason(item)}</p> : null}
              <p className="mt-1 text-xs font-medium text-amber-800 dark:text-amber-200">
                {status}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {open ? (
                  <button type="button" className="rounded border border-amber-400 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-950" onClick={() => setDialog({ item, initial: null })}>
                    Review rows
                  </button>
                ) : null}
                {item.product_decision?.choice ? (
                  <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs" onClick={() => setDialog({ item, initial: { reopen: true } })}>
                    Reopen
                  </button>
                ) : null}
                <button type="button" className="rounded border border-slate-300 px-2 py-1 text-xs" onClick={() => onShowInGrid?.(item)}>
                  Show in Compliance Review
                </button>
              </div>
            </article>
          );
        })}
      </div>
      {dialog ? (
        <Suspense fallback={null}>
          <RfpProductDecisionDialog
            jobId={jobId}
            item={dialog.item}
            initial={dialog.initial}
            onClose={() => setDialog(null)}
            onApplied={onApplied}
          />
        </Suspense>
      ) : null}
    </section>
  );
}
